// Data service - Handles both local storage and Supabase
import supabase from '../lib/supabase';

class DataService {
  constructor() {
    this.initializeData();
  }

  initializeData() {
    if (!localStorage.getItem('teachers')) {
      const sampleData = [
        {
          id: '1',
          reg_no: 'T-0001',
          full_name: 'Amina Saleh',
          school: 'Al-Noor Elementary School',
          city: 'Riyadh',
          governorate: 'amman',
          email: 'amina.saleh@school.edu',
          phone: '+966501234567',
          originality: 5,
          relevance: 4,
          skills: 5,
          presentation: 5,
          educational_value: 4,
          assignments_completed: true,
          last_updated: new Date().toISOString()
        },
        {
          id: '2',
          reg_no: 'T-0002',
          full_name: 'Omar Haddad',
          school: 'Future Leaders School',
          city: 'Jeddah',
          governorate: 'zarqa',
          email: 'omar.haddad@school.edu',
          phone: '+966507654321',
          originality: 3,
          relevance: 3,
          skills: 3,
          presentation: 3,
          educational_value: 3,
          assignments_completed: true,
          last_updated: new Date().toISOString()
        },
        {
          id: '3',
          reg_no: 'T-0003',
          full_name: 'Laila Nassar',
          school: 'Innovation Academy',
          city: 'Dammam',
          governorate: 'irbid',
          email: 'laila.nassar@school.edu',
          phone: '+966509876543',
          originality: 5,
          relevance: 5,
          skills: 5,
          presentation: 5,
          educational_value: 5,
          assignments_completed: true,
          last_updated: new Date().toISOString()
        }
      ];
      localStorage.setItem('teachers', JSON.stringify(sampleData));
    }

    // Initialize Supabase with sample data if empty
    this.initSupabaseData();
  }

  async initSupabaseData() {
    try {
      // Check if teachers table has any data
      const { data, error } = await supabase.from('teachers_x7a9b3c4d2').select('id').limit(1);
      if (error) {
        console.error('Error checking Supabase teachers:', error);
        return;
      }

      // If no data, populate with sample data
      if (data && data.length === 0) {
        const sampleData = JSON.parse(localStorage.getItem('teachers') || '[]');
        if (sampleData.length > 0) {
          // Remove client-side IDs before inserting
          const dataForInsert = sampleData.map(teacher => {
            const { id, ...teacherWithoutId } = teacher;
            return teacherWithoutId;
          });
          const { error: insertError } = await supabase.from('teachers_x7a9b3c4d2').insert(dataForInsert);
          if (insertError) {
            console.error('Error populating Supabase with sample data:', insertError);
          } else {
            console.log('Initialized Supabase with sample data');
          }
        }
      }
    } catch (err) {
      console.error('Error in initSupabaseData:', err);
    }
  }

  calculateScores(teacher) {
    if (!teacher) return null;

    // Ensure all required fields are numbers
    const originality = Number(teacher.originality) || 0;
    const relevance = Number(teacher.relevance) || 0;
    const skills = Number(teacher.skills) || 0;
    const presentation = Number(teacher.presentation) || 0;
    const educational_value = Number(teacher.educational_value) || 0;

    // Set assignments_completed to true if undefined (for backward compatibility)
    const assignments_completed = teacher.assignments_completed !== undefined ? teacher.assignments_completed : true;

    const total_raw = originality + relevance + skills + presentation + educational_value;
    const total_pct = Math.round(total_raw * 4); // Convert from 25 to 100 scale

    // Determine status code - now considering assignments completion status
    let status_code;
    if (!assignments_completed) {
      status_code = 'FAIL'; // Force FAIL status if assignments are not completed
    } else if (total_pct >= 85) {
      status_code = 'TOP_DISTINGUISHED';
    } else if (total_pct >= 50) {
      status_code = 'PASSED';
    } else {
      status_code = 'FAIL';
    }

    return {
      ...teacher,
      originality,
      relevance,
      skills,
      presentation,
      educational_value,
      assignments_completed,
      total_raw,
      total_pct,
      status_code,
      last_updated: teacher.last_updated || new Date().toISOString()
    };
  }

  async getAllTeachers() {
    try {
      console.log("Getting all teachers...");
      // First try to get from Supabase
      const { data, error } = await supabase.from('teachers_x7a9b3c4d2').select('*');
      
      if (error) {
        console.error('Error fetching from Supabase:', error);
        throw error;
      }
      
      if (data && Array.isArray(data)) {
        console.log("Fetched teachers from Supabase:", data.length);
        // Save to localStorage for offline access
        localStorage.setItem('teachers', JSON.stringify(data));
        return data.map(teacher => this.calculateScores(teacher));
      } else {
        console.warn("No data returned from Supabase or data is not an array");
        throw new Error("Invalid data format from Supabase");
      }
    } catch (err) {
      console.error('Falling back to localStorage due to error:', err);
      // Fallback to localStorage
      const teachers = JSON.parse(localStorage.getItem('teachers') || '[]');
      console.log("Using teachers from localStorage:", teachers.length);
      return teachers.map(teacher => this.calculateScores(teacher));
    }
  }

  getTeacherByRegNo(reg_no) {
    if (!reg_no) return null;
    
    const teachers = JSON.parse(localStorage.getItem('teachers') || '[]');
    const teacher = teachers.find(teacher => teacher.reg_no === reg_no);
    
    if (teacher) {
      console.log("Found teacher in localStorage:", teacher.reg_no);
      return this.calculateScores(teacher);
    }
    
    console.log("Teacher not found in localStorage:", reg_no);
    return null;
  }

  getTeacherById(id) {
    if (!id) return null;
    
    const teachers = JSON.parse(localStorage.getItem('teachers') || '[]');
    const teacher = teachers.find(teacher => teacher.id === id);
    
    return teacher ? this.calculateScores(teacher) : null;
  }

  saveTeacher(teacherData) {
    const teachers = JSON.parse(localStorage.getItem('teachers') || '[]');
    const calculatedTeacher = this.calculateScores(teacherData);
    
    if (teacherData.id) {
      // Update existing
      const index = teachers.findIndex(t => t.id === teacherData.id);
      if (index !== -1) {
        teachers[index] = calculatedTeacher;
      }
    } else {
      // Create new
      calculatedTeacher.id = Date.now().toString();
      teachers.push(calculatedTeacher);
    }
    
    localStorage.setItem('teachers', JSON.stringify(teachers));
    return calculatedTeacher;
  }

  deleteTeacher(id) {
    const teachers = JSON.parse(localStorage.getItem('teachers') || '[]');
    const filteredTeachers = teachers.filter(teacher => teacher.id !== id);
    localStorage.setItem('teachers', JSON.stringify(filteredTeachers));
    
    // Also try to delete from Supabase
    try {
      supabase.from('teachers_x7a9b3c4d2').delete().eq('id', id);
    } catch (error) {
      console.error('Error deleting from Supabase:', error);
    }
  }

  validateTeacher(teacherData) {
    const errors = {};
    
    if (!teacherData.reg_no?.trim()) {
      errors.reg_no = 'requiredField';
    }
    
    if (!teacherData.full_name?.trim()) {
      errors.full_name = 'requiredField';
    }
    
    // Check for duplicate registration number
    const teachers = JSON.parse(localStorage.getItem('teachers') || '[]');
    const existing = teachers.find(t => t.reg_no === teacherData.reg_no && t.id !== teacherData.id);
    if (existing) {
      errors.reg_no = 'duplicateRegistration';
    }
    
    // Validate scores
    const scoreFields = ['originality', 'relevance', 'skills', 'presentation', 'educational_value'];
    scoreFields.forEach(field => {
      const value = parseInt(teacherData[field]);
      if (isNaN(value) || value < 0 || value > 5) {
        errors[field] = 'invalidScore';
      }
    });
    
    // Validate email if provided
    if (teacherData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(teacherData.email)) {
      errors.email = 'invalidEmail';
    }
    
    return errors;
  }

  recalculateAll() {
    const teachers = JSON.parse(localStorage.getItem('teachers') || '[]');
    const recalculatedTeachers = teachers.map(teacher => this.calculateScores(teacher));
    localStorage.setItem('teachers', JSON.stringify(recalculatedTeachers));
    return recalculatedTeachers;
  }

  exportToCSV() {
    const teachers = JSON.parse(localStorage.getItem('teachers') || '[]').map(teacher => this.calculateScores(teacher));
    
    const headers = [
      'reg_no',
      'full_name',
      'school',
      'city',
      'governorate',
      'email',
      'phone',
      'originality',
      'relevance',
      'skills',
      'presentation',
      'educational_value',
      'assignments_completed',
      'total_raw',
      'total_pct',
      'status_code',
      'last_updated'
    ];
    
    const csvContent = [
      headers.join(','),
      ...teachers.map(teacher => 
        headers.map(header => {
          let value = teacher[header] || '';
          
          // Convert boolean to string
          if (header === 'assignments_completed') {
            value = teacher[header] === true ? 'true' : 'false';
          }
          
          if (typeof value === 'string' && value.includes(',')) {
            value = `"${value}"`;
          }
          
          return value;
        }).join(',')
      )
    ].join('\n');
    
    return csvContent;
  }

  importFromCSV(csvContent) {
    console.log("Starting CSV import with content length:", csvContent.length);
    
    // Split by lines, filtering out empty lines
    const lines = csvContent.split('\n').filter(line => line.trim());
    console.log("Found lines:", lines.length);
    
    if (lines.length < 2) {
      console.error("CSV has fewer than 2 lines");
      return [];
    }
    
    try {
      // Parse headers, removing quotes if present
      const headers = this.parseCSVLine(lines[0]);
      console.log("Parsed headers:", headers);
      
      const teachers = [];
      
      // Process each data row
      for (let i = 1; i < lines.length; i++) {
        try {
          // Parse the CSV line into values
          const values = this.parseCSVLine(lines[i]);
          
          if (values.length !== headers.length) {
            console.warn(`Line ${i} has ${values.length} values but expected ${headers.length}. Skipping.`);
            continue;
          }
          
          // Create teacher object from headers and values
          const teacher = {};
          headers.forEach((header, index) => {
            let value = values[index] || '';
            
            // Handle assignments_completed field specifically
            if (header === 'assignments_completed') {
              // Convert string to boolean
              teacher[header] = value.toLowerCase() === 'true';
            } 
            // Convert numeric fields to numbers
            else if (['originality', 'relevance', 'skills', 'presentation', 'educational_value'].includes(header)) {
              teacher[header] = parseInt(value) || 0;
            } else {
              teacher[header] = value;
            }
          });
          
          // Generate new ID
          teacher.id = Date.now().toString() + '-' + i;
          
          // Make sure required fields have default values
          if (!teacher.last_updated) {
            teacher.last_updated = new Date().toISOString();
          }
          
          if (teacher.assignments_completed === undefined) {
            teacher.assignments_completed = true;
          }
          
          teachers.push(teacher);
          console.log(`Processed teacher ${i}:`, teacher.reg_no);
        } catch (lineError) {
          console.error(`Error processing line ${i}:`, lineError);
        }
      }
      
      console.log("Successfully parsed teachers:", teachers.length);
      
      // Save imported teachers
      if (teachers.length > 0) {
        const existingTeachers = JSON.parse(localStorage.getItem('teachers') || '[]');
        const allTeachers = [...existingTeachers, ...teachers];
        localStorage.setItem('teachers', JSON.stringify(allTeachers));
        
        // Also try to save to Supabase
        try {
          // Remove client-side IDs before inserting
          const dataForInsert = teachers.map(teacher => {
            const { id, ...teacherWithoutId } = teacher;
            return teacherWithoutId;
          });
          
          if (dataForInsert.length > 0) {
            supabase.from('teachers_x7a9b3c4d2').insert(dataForInsert);
          }
        } catch (error) {
          console.error('Error importing to Supabase:', error);
        }
      }
      
      return teachers;
    } catch (error) {
      console.error('Error importing CSV:', error);
      throw new Error('Failed to parse CSV: ' + error.message);
    }
  }

  // Helper method to properly parse CSV lines with quoted values
  parseCSVLine(line) {
    const result = [];
    let currentValue = '';
    let insideQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        // Toggle quote state
        insideQuotes = !insideQuotes;
      } else if (char === ',' && !insideQuotes) {
        // End of field
        result.push(currentValue);
        currentValue = '';
      } else {
        // Add character to current field
        currentValue += char;
      }
    }
    
    // Add the last field
    result.push(currentValue);
    
    // Clean up any quotes from the values
    return result.map(val => val.replace(/^"|"$/g, '').trim());
  }
}

export default new DataService();